import React, { PureComponent, Fragment } from 'react';
import {
    oneOfType,
    string,
    arrayOf,
    shape,
    bool,
    number,
    func,
} from 'prop-types';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { ListItem, SearchInput } from 'components';
import { intlShape } from 'shapes';
import classnames from 'classnames';

const searchInputPlaceholder = defineMessages({
    id: 'listing.filters.inputSearch.placeholder',
    defaultMessage: 'Search here...',
    description: 'label of filter search input placeholder',
});

class CommonRefinementList extends PureComponent {
    static propTypes = {
        items: arrayOf(
            shape({
                count: number,
                isRefined: bool,
                label: string,
                value: oneOfType([string, arrayOf(string)]),
            }),
        ).isRequired,
        label: string.isRequired,
        attribute: string.isRequired,
        onSelectItem: func.isRequired,
        searchForItems: func.isRequired,
        isSearchable: bool,
        nbOfDisplayedResults: number.isRequired,
        onShowMoreResults: func.isRequired,
        inputSearchClassName: string,
        intl: intlShape.isRequired,
    };

    static defaultProps = {
        isSearchable: false,
        inputSearchClassName: null,
    };

    constructor(props) {
        super(props);
        this.state = {
            filterQuery: '',
        };

        this.initialResultsLimit = props.nbOfDisplayedResults;
        this.initialItemLength = props.items.length;
    }

    onSearch = (value, evt) => {
        this.setState({ filterQuery: value });
        this.props.searchForItems(value, evt);
    };

    handleSelect = (value, evt) => {
        evt.stopPropagation();
        if (this.props.attribute) evt.target.name = this.props.attribute;
        this.props.onSelectItem(value, this.state.filterQuery);
    };

    shouldShowMoreButton = () => {
        const { items, nbOfDisplayedResults } = this.props;
        return (
            items.length >= this.initialResultsLimit &&
            nbOfDisplayedResults < items.length
        );
    };

    /**
     * we compare with initial items.length because when we filter with query
     * in input, items.length change and if there is less than limit results
     * the input search will hide
     */
    shouldShowSearchInput = () => {
        const { isSearchable } = this.props;
        return (
            isSearchable && this.initialItemLength >= this.initialResultsLimit
        );
    };

    render() {
        const {
            items,
            label,
            nbOfDisplayedResults,
            onShowMoreResults,
            inputSearchClassName,
            intl,
        } = this.props;

        const buttonClassnames = classnames('refinementList__moreButton', {
            'refinementList__moreButton--hidden':
                nbOfDisplayedResults >= items.length,
        });

        return (
            <Fragment>
                {this.shouldShowSearchInput() && (
                    <SearchInput
                        className={inputSearchClassName}
                        placeholder={intl.formatMessage(searchInputPlaceholder)}
                        onSearch={this.onSearch}
                    />
                )}

                {items.slice(0, nbOfDisplayedResults).map((item, index) => (
                    <ListItem
                        key={item.label}
                        index={index}
                        value={item.label}
                        checked={item.isRefined}
                        label={item.label}
                        count={item.count}
                        dataQa="listItemCta"
                        onClick={this.handleSelect}
                    />
                ))}

                {this.shouldShowMoreButton() && (
                    <button
                        className={buttonClassnames}
                        onClick={onShowMoreResults}
                        data-qa="moreItemsCta"
                    >
                        <FormattedMessage
                            id="listing.filters.disjunctiveFilter.moreResultButtonLabel"
                            defaultMessage="More {name}"
                            description="Button to show more items in list of disjunctive filter"
                            values={{
                                name: label,
                            }}
                        />
                    </button>
                )}

                {items.length === 0 && (
                    <div className="refinementList__noResults">
                        <FormattedMessage
                            id="listing.filters.noResult"
                            defaultMessage="No result"
                            description="No result label"
                        />
                    </div>
                )}
            </Fragment>
        );
    }
}

export default injectIntl(CommonRefinementList);
